import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, Alert, IconButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import supabase from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';

export default function Auth() {
  const { user, signIn, signUp, signOut } = useAuth();
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Profile/user name edit state (must be declared unconditionally for hooks order)
  const [username, setUsername] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');
  const [status, setStatus] = useState('');
  const [profileError, setProfileError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      if (mode === 'signin') {
        const { error } = await signIn(email, password);
        if (error) throw error;
        setMessage('Signed in');
      } else {
        const { error } = await signUp(email, password);
        if (error) throw error;
        setMessage('Check your email to confirm (if required)');
      }
    } catch (err) {
      setError(err?.message || 'Authentication error');
    }
  };

  // Load profile username when user changes (and reset when signed out)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!user) {
        if (!mounted) return;
        setUsername('');
        setOriginalUsername('');
        setStatus('');
        setProfileError('');
        return;
      }
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', user.id)
          .single();
        if (!mounted) return;
        if (error && error.code !== 'PGRST116') {
          setProfileError(error.message || 'Failed to load profile');
        } else {
          const n = data?.name || '';
          setUsername(n);
          setOriginalUsername(n);
        }
      } catch (e) {
        if (mounted) setProfileError(e?.message || 'Failed to load profile');
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  const handleSaveUsername = async () => {
    if (!user) return;
    setSaving(true);
    setStatus('');
    setProfileError('');
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({ id: user.id, name: username ?? '' }, { onConflict: 'id' });
      if (error) throw error;
      setOriginalUsername(username ?? '');
      setStatus('Saved');
    } catch (e) {
      setProfileError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelUsername = () => {
    setUsername(originalUsername);
    setStatus('');
    setProfileError('');
  };

  if (user) {
    return (
      <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
        <Typography variant="h5" gutterBottom>Account</Typography>
        <Typography sx={{ mb: 2 }}>Signed in as {user.email}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <TextField
            label="User name"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Add a user name"
            size="small"
            sx={{ minWidth: 260 }}
          />
          <IconButton aria-label="Save user name" color="primary" onClick={handleSaveUsername} disabled={saving || username === originalUsername}>
            <SaveIcon />
          </IconButton>
          <IconButton aria-label="Cancel" onClick={handleCancelUsername} disabled={saving || username === originalUsername}>
            <CloseIcon />
          </IconButton>
        </Box>
        {profileError && <Alert severity="error" sx={{ mb: 1 }}>{profileError}</Alert>}
        {status && <Alert severity="success" sx={{ mb: 1 }}>{status}</Alert>}

        <Button
          variant="outlined"
          onClick={() => signOut()}
          sx={{
            mt: 2,
            color: '#EAD7B2',
            borderColor: '#EAD7B2',
            '&:hover': { borderColor: '#EAD7B2', backgroundColor: 'rgba(234, 215, 178, 0.1)' },
          }}
        >
          Sign out
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 420, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        {mode === 'signin' ? 'Sign In' : 'Create Account'}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <Button type="submit" variant="contained">
          {mode === 'signin' ? 'Sign In' : 'Sign Up'}
        </Button>
      </Box>
      <Button onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')} sx={{ mt: 2 }}>
        {mode === 'signin' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
      </Button>
    </Box>
  );
}
